import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'
import { Heading, Text, Button, Input, Select, LoadingSpinner } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { useUserLogs } from '../../hooks/useUserLogs'
import { marketingService } from '../../services/marketing'
import { BRAND_COLOR } from '../../constants'
import type {
  ClippingSearchKeywordOperator,
  ClippingSearchSiteCollectorType,
  ClippingSearchSitePurpose,
  MarketingClippingCategory,
  MarketingClippingSearchConfig,
  MarketingClippingSearchSite,
  MarketingClippingVehicle,
} from '../../types/marketing'
import ClippingAnaliseIaTab from './ClippingAnaliseIaTab'


const MODULE_NAME_DEFAULT = 'marketing_parametros'
/** Suprime o texto padrão "Carregando permissões..." — a tela já exibe loading próprio dos dados. */
const CLIPPING_PERM_LOADING: React.ReactNode = null

interface ClippingBuscaAutomaticaTabProps {
  moduleName?: string
  purpose: ClippingSearchSitePurpose
  /** Quando definido, exibe apenas o painel indicado (sem subnavegação interna). */
  onlyPanel?: 'configs'
  categorias: MarketingClippingCategory[]
  veiculos: MarketingClippingVehicle[]
  onOpenCatalog: (kind: 'veiculo' | 'categoria', context: 'search_config' | 'search_site') => void
  pendingCatalogSelection: {
    kind: 'veiculo' | 'categoria'
    id: string
    context: 'search_config' | 'search_site'
  } | null
  onPendingCatalogSelectionConsumed: () => void
}

interface TagInputProps {
  label?: React.ReactNode
  values: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  disabled?: boolean
  error?: string
}

const TagInput: React.FC<TagInputProps> = ({
  label,
  values,
  onChange,
  placeholder,
  disabled,
  error,
}) => {
  const [draft, setDraft] = useState('')

  const addTag = () => {
    const value = draft.trim()
    if (!value) return
    if (values.some((v) => v.toLowerCase() === value.toLowerCase())) {
      setDraft('')
      return
    }
    onChange([...values, value])
    setDraft('')
  }

  return (
    <div>
      {label && (
        <Text as="label" size="sm" className="mb-1 block">
          {label}
        </Text>
      )}
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {values.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full bg-primary-50 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300 px-2.5 py-0.5 text-xs font-medium"
            >
              {tag}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onChange(values.filter((v) => v !== tag))}
                  className="hover:text-red-500"
                  aria-label={`Remover ${tag}`}
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      <Input
        value={draft}
        disabled={disabled}
        placeholder={placeholder || 'Digite e pressione Enter...'}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            addTag()
          }
        }}
        onBlur={addTag}
        className={error ? 'border-red-500' : ''}
      />
      {error && (
        <Text size="sm" className="text-red-500 mt-1">
          {error}
        </Text>
      )}
    </div>
  )
}

function formatDateTimeBr(value: string | null): string {
  if (!value) return '—'
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function operatorLabel(op: ClippingSearchKeywordOperator): string {
  return op === 'and' ? 'e' : 'ou'
}

function runStatusLabel(status: MarketingClippingSearchConfig['last_run_status']): string {
  if (status === 'success') return 'Sucesso'
  if (status === 'failed') return 'Falhou'
  if (status === 'running') return 'Em execução'
  return 'Nunca executou'
}

function runStatusClass(status: MarketingClippingSearchConfig['last_run_status']): string {
  if (status === 'success') {
    return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
  }
  if (status === 'failed') {
    return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
  }
  if (status === 'running') {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
  }
  return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
}

function sortSites(list: MarketingClippingSearchSite[]): MarketingClippingSearchSite[] {
  return [...list].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
}

function sortConfigs(list: MarketingClippingSearchConfig[]): MarketingClippingSearchConfig[] {
  return [...list].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
}

function snapshotSite(site: MarketingClippingSearchSite) {
  return {
    name: site.name,
    url: site.url,
    collector_type: site.collector_type,
    purpose: site.purpose,
    vehicle_id: site.vehicle_id,
    is_active: site.is_active,
    notes: site.notes,
  }
}

function snapshotConfig(config: MarketingClippingSearchConfig) {
  return {
    name: config.name,
    is_active: config.is_active,
    keywords: config.keywords,
    keyword_operator: config.keyword_operator,
    exclude_keywords: config.exclude_keywords,
    default_category_id: config.default_category_id,
    notes: config.notes,
  }
}

function collectorTypeLabel(type: ClippingSearchSiteCollectorType): string {
  return type === 'puppeteer' ? 'Pesquisa' : 'RSS'
}

function collectorTypeClass(type: ClippingSearchSiteCollectorType): string {
  return type === 'puppeteer'
    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
    : 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300'
}

function purposeLabel(purpose: ClippingSearchSitePurpose): string {
  return purpose === 'alerta' ? 'Alerta' : 'Clipping'
}

function purposeClass(purpose: ClippingSearchSitePurpose): string {
  return purpose === 'alerta'
    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
    : 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
}

interface SiteFormState {
  name: string
  url: string
  collector_type: ClippingSearchSiteCollectorType
  purpose: ClippingSearchSitePurpose
  vehicle_id: string
  notes: string
}

const emptySiteForm = (purpose: ClippingSearchSitePurpose): SiteFormState => ({
  name: '',
  url: '',
  collector_type: 'rss',
  purpose,
  vehicle_id: '',
  notes: '',
})

interface ConfigFormState {
  id?: string
  name: string
  keywords: string[]
  keyword_operator: ClippingSearchKeywordOperator
  exclude_keywords: string[]
  default_category_id: string
  notes: string
  is_active: boolean
}

const emptyConfigForm = (): ConfigFormState => ({
  name: '',
  keywords: [],
  keyword_operator: 'or',
  exclude_keywords: [],
  default_category_id: '',
  notes: '',
  is_active: true,
})

const ClippingBuscaAutomaticaTab: React.FC<ClippingBuscaAutomaticaTabProps> = ({
  moduleName = MODULE_NAME_DEFAULT,
  purpose,
  onlyPanel,
  categorias,
  veiculos,
  onOpenCatalog,
  pendingCatalogSelection,
  onPendingCatalogSelectionConsumed,
}) => {
  const { logCreate, logUpdate, logDelete } = useUserLogs()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [sites, setSites] = useState<MarketingClippingSearchSite[]>([])
  const [configs, setConfigs] = useState<MarketingClippingSearchConfig[]>([])

  const [siteForm, setSiteForm] = useState<SiteFormState>(() => emptySiteForm(purpose))
  const [siteEditingId, setSiteEditingId] = useState<string | null>(null)
  const [isSiteModalOpen, setIsSiteModalOpen] = useState(false)
  const [siteSaving, setSiteSaving] = useState(false)
  const [siteTogglingId, setSiteTogglingId] = useState<string | null>(null)
  const [siteError, setSiteError] = useState<string | null>(null)

  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false)
  const [configForm, setConfigForm] = useState<ConfigFormState>(emptyConfigForm())
  const [configSaving, setConfigSaving] = useState(false)
  const [configTogglingId, setConfigTogglingId] = useState<string | null>(null)
  const [configValidation, setConfigValidation] = useState<Record<string, string>>({})
  const [configError, setConfigError] = useState<string | null>(null)

  const activeCategorias = useMemo(() => categorias.filter((c) => c.is_active), [categorias])
  const activeVeiculos = useMemo(() => veiculos.filter((v) => v.is_active), [veiculos])
  const configsOnly = onlyPanel === 'configs'
  /** Temporariamente oculto — reativar quando a feature estiver pronta. */
  const showAnaliseIaTab = false
  const showInnerNav = purpose === 'clipping' && !configsOnly && showAnaliseIaTab
  const [clippingInnerTab, setClippingInnerTab] = useState<'sites' | 'analise_ia'>('sites')

  const innerTabClass = (tab: 'sites' | 'analise_ia') =>
    `py-2.5 px-1 border-b-2 font-medium text-sm ${
      clippingInnerTab === tab
        ? 'border-primary-600 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
    }`

  useEffect(() => {
    if (!pendingCatalogSelection) return
    if (pendingCatalogSelection.context === 'search_config' && pendingCatalogSelection.kind === 'categoria') {
      setConfigForm((prev) => ({ ...prev, default_category_id: pendingCatalogSelection.id }))
    }
    if (pendingCatalogSelection.context === 'search_site' && pendingCatalogSelection.kind === 'veiculo') {
      setSiteForm((prev) => ({ ...prev, vehicle_id: pendingCatalogSelection.id }))
    }
    onPendingCatalogSelectionConsumed()
  }, [pendingCatalogSelection, onPendingCatalogSelectionConsumed])

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      if (configsOnly) {
        const configsData = await marketingService.listSearchConfigs()
        setSites([])
        setConfigs(sortConfigs(configsData))
      } else if (purpose === 'clipping') {
        const sitesData = await marketingService.listSearchSites(false, purpose)
        setSites(sortSites(sitesData))
        setConfigs([])
      } else {
        const sitesData = await marketingService.listSearchSites(false, purpose)
        setSites(sortSites(sitesData))
        setConfigs([])
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar busca'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [purpose, configsOnly])

  useEffect(() => {
    setIsSiteModalOpen(false)
    setSiteEditingId(null)
    setSiteForm(emptySiteForm(purpose))
    setSiteError(null)
    setClippingInnerTab('sites')
    void loadData()
  }, [loadData, purpose])

  const closeSiteModal = () => {
    setIsSiteModalOpen(false)
    setSiteEditingId(null)
    setSiteForm(emptySiteForm(purpose))
    setSiteError(null)
  }

  const openNewSite = () => {
    setSiteEditingId(null)
    setSiteForm(emptySiteForm(purpose))
    setSiteError(null)
    setIsSiteModalOpen(true)
  }

  const openEditSite = (site: MarketingClippingSearchSite) => {
    setSiteEditingId(site.id)
    setSiteForm({
      name: site.name,
      url: site.url,
      collector_type: site.collector_type || 'rss',
      purpose: site.purpose || purpose,
      vehicle_id: site.vehicle_id || '',
      notes: site.notes || '',
    })
    setSiteError(null)
    setIsSiteModalOpen(true)
  }

  const saveSite = async () => {
    const name = siteForm.name.trim()
    const url = siteForm.url.trim()
    if (!name) {
      setSiteError('Informe o nome')
      return
    }
    if (!url) {
      setSiteError('Informe a URL')
      return
    }
    if (!siteForm.collector_type) {
      setSiteError('Informe o tipo de coleta')
      return
    }
    if (!siteForm.purpose) {
      setSiteError('Informe o tipo (alerta ou clipping)')
      return
    }
    try {
      setSiteSaving(true)
      setSiteError(null)
      if (siteEditingId) {
        const previous = sites.find((s) => s.id === siteEditingId)
        const updated = await marketingService.updateSearchSite(siteEditingId, {
          name,
          url,
          collector_type: siteForm.collector_type,
          purpose: siteForm.purpose,
          vehicle_id: siteForm.vehicle_id || null,
          notes: siteForm.notes.trim() || null,
        })
        setSites((prev) => {
          const next = prev.map((s) => (s.id === updated.id ? updated : s))
          return sortSites(next.filter((s) => s.purpose === purpose))
        })
        void logUpdate(
          'marketing_clipping_search_site',
          updated.id,
          `Atualizou site de busca automática: ${updated.name}`,
          previous ? snapshotSite(previous) : undefined,
          snapshotSite(updated)
        )
        closeSiteModal()
      } else {
        const created = await marketingService.createSearchSite({
          name,
          url,
          collector_type: siteForm.collector_type,
          purpose: siteForm.purpose,
          vehicle_id: siteForm.vehicle_id || null,
          notes: siteForm.notes.trim() || null,
        })
        if (created.purpose === purpose) {
          setSites((prev) => sortSites([...prev, created]))
        }
        void logCreate(
          'marketing_clipping_search_site',
          created.id,
          `Criou site de busca automática: ${created.name}`,
          snapshotSite(created)
        )
        closeSiteModal()
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar site'
      setSiteError(message)
    } finally {
      setSiteSaving(false)
    }
  }

  const toggleSiteActive = async (site: MarketingClippingSearchSite) => {
    const nextActive = !site.is_active
    try {
      setSiteTogglingId(site.id)
      setSiteError(null)
      const updated = await marketingService.updateSearchSite(site.id, { is_active: nextActive })
      setSites((prev) => sortSites(prev.map((s) => (s.id === updated.id ? updated : s))))
      void logUpdate(
        'marketing_clipping_search_site',
        updated.id,
        `${nextActive ? 'Ativou' : 'Inativou'} site de busca automática: ${updated.name}`,
        snapshotSite(site),
        snapshotSite(updated)
      )
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao atualizar status do site'
      setSiteError(message)
    } finally {
      setSiteTogglingId(null)
    }
  }

  const openNewConfig = () => {
    setConfigForm(emptyConfigForm())
    setConfigValidation({})
    setConfigError(null)
    setIsConfigModalOpen(true)
  }

  const openEditConfig = (config: MarketingClippingSearchConfig) => {
    setConfigForm({
      id: config.id,
      name: config.name,
      keywords: config.keywords || [],
      keyword_operator: config.keyword_operator || 'or',
      exclude_keywords: config.exclude_keywords || [],
      default_category_id: config.default_category_id || '',
      notes: config.notes || '',
      is_active: config.is_active,
    })
    setConfigValidation({})
    setConfigError(null)
    setIsConfigModalOpen(true)
  }

  const closeConfigModal = () => {
    setIsConfigModalOpen(false)
    setConfigForm(emptyConfigForm())
    setConfigValidation({})
    setConfigError(null)
  }

  const validateConfigForm = (): boolean => {
    const errors: Record<string, string> = {}
    if (!configForm.name.trim()) errors.name = 'Informe o nome'
    if (configForm.keywords.length === 0) errors.keywords = 'Adicione ao menos uma palavra-chave'
    setConfigValidation(errors)
    return Object.keys(errors).length === 0
  }

  const saveConfig = async () => {
    if (!validateConfigForm()) return
    try {
      setConfigSaving(true)
      setConfigError(null)
      setError(null)
      if (configForm.id) {
        const previous = configs.find((c) => c.id === configForm.id)
        const updated = await marketingService.updateSearchConfig({
          id: configForm.id,
          name: configForm.name.trim(),
          keywords: configForm.keywords,
          keyword_operator: configForm.keyword_operator,
          exclude_keywords: configForm.exclude_keywords,
          default_category_id: configForm.default_category_id || null,
          notes: configForm.notes.trim() || null,
          is_active: configForm.is_active,
        })
        setConfigs((prev) => sortConfigs(prev.map((c) => (c.id === updated.id ? updated : c))))
        void logUpdate(
          'marketing_clipping_search_config',
          updated.id,
          `Atualizou config de busca automática: ${updated.name}`,
          previous ? snapshotConfig(previous) : undefined,
          snapshotConfig(updated)
        )
      } else {
        const created = await marketingService.createSearchConfig({
          name: configForm.name.trim(),
          keywords: configForm.keywords,
          keyword_operator: configForm.keyword_operator,
          exclude_keywords: configForm.exclude_keywords,
          default_category_id: configForm.default_category_id || null,
          notes: configForm.notes.trim() || null,
          is_active: configForm.is_active,
        })
        setConfigs((prev) => sortConfigs([...prev, created]))
        void logCreate(
          'marketing_clipping_search_config',
          created.id,
          `Criou config de busca automática: ${created.name}`,
          snapshotConfig(created)
        )
      }
      closeConfigModal()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar configuração'
      setConfigError(message)
    } finally {
      setConfigSaving(false)
    }
  }

  const toggleConfigActive = async (config: MarketingClippingSearchConfig) => {
    const nextActive = !config.is_active
    try {
      setConfigTogglingId(config.id)
      setError(null)
      const updated = await marketingService.updateSearchConfig({
        id: config.id,
        is_active: nextActive,
      })
      setConfigs((prev) => sortConfigs(prev.map((c) => (c.id === updated.id ? updated : c))))
      void logUpdate(
        'marketing_clipping_search_config',
        updated.id,
        `${nextActive ? 'Ativou' : 'Inativou'} config de busca automática: ${updated.name}`,
        snapshotConfig(config),
        snapshotConfig(updated)
      )
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao atualizar status'
      setError(message)
    } finally {
      setConfigTogglingId(null)
    }
  }

  const removeConfig = async (config: MarketingClippingSearchConfig) => {
    if (!window.confirm(`Excluir a configuração "${config.name}"?`)) return
    try {
      setError(null)
      await marketingService.deleteSearchConfig(config.id)
      setConfigs((prev) => prev.filter((c) => c.id !== config.id))
      void logDelete(
        'marketing_clipping_search_config',
        config.id,
        `Excluiu config de busca automática: ${config.name}`,
        snapshotConfig(config)
      )
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao excluir configuração'
      setError(message)
    }
  }

  if (loading) {
    return (
      <div className="py-16 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {showInnerNav && (
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8" aria-label="Subabas de busca de clippings">
            <button
              type="button"
              onClick={() => setClippingInnerTab('sites')}
              className={innerTabClass('sites')}
            >
              Sites a monitorar
            </button>
            {showAnaliseIaTab && (
              <button
                type="button"
                onClick={() => setClippingInnerTab('analise_ia')}
                className={innerTabClass('analise_ia')}
              >
                Análise de IA
              </button>
            )}
          </nav>
        </div>
      )}

      {!configsOnly && (!showInnerNav || clippingInnerTab === 'sites') && (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            {!showInnerNav && (
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                {purpose === 'clipping' ? 'Sites de clippings' : 'Sites de alertas'}
              </Heading>
            )}
            <Text size="sm" color="muted" className={showInnerNav ? undefined : 'mt-1'}>
              {purpose === 'clipping'
                ? 'Informe os sites em que a IA deve buscar notícias para gerar clippings.'
                : 'Informe os sites em que a IA deve buscar notícias para gerar alertas.'}
            </Text>
          </div>
          <PermissionGuard
            moduleName={moduleName}
            permissionType="insert"
            fallback={null}
            loadingComponent={CLIPPING_PERM_LOADING}
          >
            <Button
              onClick={openNewSite}
              className="flex items-center gap-1 shrink-0"
              style={{ backgroundColor: BRAND_COLOR }}
            >
              <PlusIcon className="h-5 w-5" />
              Novo site
            </Button>
          </PermissionGuard>
        </div>

        {siteError && !isSiteModalOpen && (
          <Text size="sm" className="text-red-500 mb-4">
            {siteError}
          </Text>
        )}

        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/30">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Nome
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  URL
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Tipo
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Coleta
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Veículo vinculado
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Status
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {sites.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    {purpose === 'clipping'
                      ? 'Nenhum site de clipping cadastrado.'
                      : 'Nenhum site de alerta cadastrado.'}
                  </td>
                </tr>
              ) : (
                sites.map((site) => (
                  <tr
                    key={site.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                      siteEditingId === site.id ? 'bg-primary-50/60 dark:bg-primary-900/20' : ''
                    }`}
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-medium">
                      {site.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-[280px]">
                      <a
                        href={site.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 dark:text-primary-400 hover:underline truncate block"
                        title={site.url}
                      >
                        {site.url}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${purposeClass(
                          site.purpose || purpose
                        )}`}
                      >
                        {purposeLabel(site.purpose || purpose)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${collectorTypeClass(
                          site.collector_type || 'rss'
                        )}`}
                      >
                        {collectorTypeLabel(site.collector_type || 'rss')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {site.vehicle?.name || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          site.is_active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-300'
                        }`}
                      >
                        {site.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap space-x-2">
                      <PermissionGuard
                        moduleName={moduleName}
                        permissionType="update"
                        fallback={null}
                        loadingComponent={CLIPPING_PERM_LOADING}
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditSite(site)}
                          disabled={siteSaving || Boolean(siteTogglingId)}
                          title="Editar"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                      </PermissionGuard>
                      <PermissionGuard
                        moduleName={moduleName}
                        permissionType="update"
                        fallback={null}
                        loadingComponent={CLIPPING_PERM_LOADING}
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void toggleSiteActive(site)}
                          disabled={siteTogglingId === site.id || siteSaving}
                        >
                          {siteTogglingId === site.id
                            ? 'Salvando...'
                            : site.is_active
                              ? 'Inativar'
                              : 'Ativar'}
                        </Button>
                      </PermissionGuard>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {configsOnly && (
      <div>
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <Text size="sm" color="muted">
              Defina o que a IA deve buscar e como classificar os alertas e os clippings gerados.
            </Text>
          </div>
          <PermissionGuard
            moduleName={moduleName}
            permissionType="insert"
            fallback={null}
            loadingComponent={CLIPPING_PERM_LOADING}
          >
            <Button
              variant="solid"
              onClick={openNewConfig}
              className="flex items-center gap-1 shrink-0"
            >
              <PlusIcon className="h-5 w-5" />
              Nova configuração
            </Button>
          </PermissionGuard>
        </div>

        {configs.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-10 text-center">
            <Text size="sm" color="muted">
              Nenhuma configuração cadastrada. Crie uma busca para a IA monitorar as palavras-chave
              nos sites ativos.
            </Text>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {configs.map((config) => (
              <div
                key={config.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 flex flex-col gap-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Heading as="h4" size="xl" className="text-gray-900 dark:text-white truncate">
                      {config.name}
                    </Heading>
                  </div>
                  <span
                    className={`shrink-0 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      config.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-300'
                    }`}
                  >
                    {config.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  {(config.keywords || []).map((kw, idx) => (
                    <React.Fragment key={kw}>
                      {idx > 0 && (
                        <Text size="xs" color="muted" className="px-0.5">
                          {operatorLabel(config.keyword_operator)}
                        </Text>
                      )}
                      <span className="inline-flex items-center rounded-full bg-primary-50 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300 px-2.5 py-0.5 text-xs font-medium">
                        {kw}
                      </span>
                    </React.Fragment>
                  ))}
                </div>

                {(config.exclude_keywords || []).length > 0 && (
                  <div>
                    <Text size="xs" color="muted" className="mb-1">
                      Excluir:
                    </Text>
                    <div className="flex flex-wrap gap-1.5">
                      {(config.exclude_keywords || []).map((kw) => (
                        <span
                          key={kw}
                          className="inline-flex items-center rounded-full bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2.5 py-0.5 text-xs font-medium"
                        >
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div>
                    <Text size="xs" color="muted">
                      Categoria
                    </Text>
                    <Text size="sm" className="text-gray-900 dark:text-white">
                      {config.default_category?.name || '—'}
                    </Text>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2 pt-1 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${runStatusClass(config.last_run_status)}`}
                    >
                      {runStatusLabel(config.last_run_status)}
                    </span>
                    <Text size="xs" color="muted" className="truncate">
                      {formatDateTimeBr(config.last_run_at)}
                    </Text>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <PermissionGuard
                      moduleName={moduleName}
                      permissionType="update"
                      fallback={null}
                      loadingComponent={CLIPPING_PERM_LOADING}
                    >
                      <Button variant="outline" size="sm" onClick={() => openEditConfig(config)}>
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                    </PermissionGuard>
                    <PermissionGuard
                      moduleName={moduleName}
                      permissionType="update"
                      fallback={null}
                      loadingComponent={CLIPPING_PERM_LOADING}
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void toggleConfigActive(config)}
                        disabled={configTogglingId === config.id}
                      >
                        {configTogglingId === config.id
                          ? '...'
                          : config.is_active
                            ? 'Inativar'
                            : 'Ativar'}
                      </Button>
                    </PermissionGuard>
                    <PermissionGuard
                      moduleName={moduleName}
                      permissionType="delete"
                      fallback={null}
                      loadingComponent={CLIPPING_PERM_LOADING}
                    >
                      <Button variant="ghost" size="sm" onClick={() => void removeConfig(config)}>
                        <TrashIcon className="h-4 w-4 text-red-500" />
                      </Button>
                    </PermissionGuard>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {showAnaliseIaTab && showInnerNav && clippingInnerTab === 'analise_ia' && (
        <ClippingAnaliseIaTab />
      )}

      {isSiteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                {siteEditingId ? 'Editar site' : 'Novo site'}
              </Heading>
              <Button
                onClick={closeSiteModal}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
                disabled={siteSaving}
              >
                <XMarkIcon className="h-4 w-4" />
                Fechar
              </Button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-5">
              {siteError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
                  {siteError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Text size="sm" className="mb-2">
                    Nome <span className="text-red-500">*</span>
                  </Text>
                  <Input
                    value={siteForm.name}
                    onChange={(e) => setSiteForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex.: G1 Economia"
                  />
                </div>
                <div>
                  <Text size="sm" className="mb-2">
                    URL <span className="text-red-500">*</span>
                  </Text>
                  <Input
                    type="url"
                    value={siteForm.url}
                    onChange={(e) => setSiteForm((prev) => ({ ...prev, url: e.target.value }))}
                    placeholder="https://exemplo.com"
                  />
                </div>
                <div>
                  <Text size="sm" className="mb-2">
                    Tipo <span className="text-red-500">*</span>
                  </Text>
                  <Select
                    value={siteForm.purpose}
                    onChange={(e) =>
                      setSiteForm((prev) => ({
                        ...prev,
                        purpose: e.target.value as ClippingSearchSitePurpose,
                      }))
                    }
                  >
                    <option value="clipping">Clipping</option>
                    <option value="alerta">Alerta</option>
                  </Select>
                </div>
                <div>
                  <Text size="sm" className="mb-2">
                    Tipo de coleta <span className="text-red-500">*</span>
                  </Text>
                  <Select
                    value={siteForm.collector_type}
                    onChange={(e) =>
                      setSiteForm((prev) => ({
                        ...prev,
                        collector_type: e.target.value as ClippingSearchSiteCollectorType,
                      }))
                    }
                  >
                    <option value="rss">RSS</option>
                    <option value="puppeteer">Pesquisa (Puppeteer)</option>
                  </Select>
                </div>
              </div>

              <div>
                <Text size="sm" className="mb-2">
                  Veículo vinculado
                </Text>
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <Select
                      value={siteForm.vehicle_id}
                      onChange={(e) => setSiteForm((prev) => ({ ...prev, vehicle_id: e.target.value }))}
                    >
                      <option value="">Nenhum</option>
                      {activeVeiculos.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                        </option>
                      ))}
                      {siteForm.vehicle_id &&
                        !activeVeiculos.some((v) => v.id === siteForm.vehicle_id) && (
                          <option value={siteForm.vehicle_id}>
                            {veiculos.find((v) => v.id === siteForm.vehicle_id)?.name || 'Veículo inativo'}{' '}
                            (inativo)
                          </option>
                        )}
                    </Select>
                  </div>
                  <PermissionGuard
                    moduleName={moduleName}
                    permissionType="insert"
                    fallback={null}
                    loadingComponent={CLIPPING_PERM_LOADING}
                  >
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onOpenCatalog('veiculo', 'search_site')}
                      title="Cadastrar veículo"
                      className="shrink-0 px-2"
                      disabled={siteSaving}
                    >
                      <PlusIcon className="h-4 w-4" />
                    </Button>
                  </PermissionGuard>
                </div>
              </div>

              <div>
                <Text size="sm" className="mb-2">
                  Observações
                </Text>
                <Input
                  value={siteForm.notes}
                  onChange={(e) => setSiteForm((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Opcional"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <Button onClick={closeSiteModal} variant="outline" disabled={siteSaving}>
                Cancelar
              </Button>
              <PermissionGuard
                moduleName={moduleName}
                permissionType={siteEditingId ? 'update' : 'insert'}
                fallback={null}
                loadingComponent={CLIPPING_PERM_LOADING}
              >
                <Button
                  onClick={() => void saveSite()}
                  disabled={siteSaving || !siteForm.name.trim() || !siteForm.url.trim()}
                  className="flex items-center gap-2"
                  style={{ backgroundColor: BRAND_COLOR }}
                >
                  {siteSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="h-4 w-4" />
                      {siteEditingId ? 'Atualizar' : 'Criar'}
                    </>
                  )}
                </Button>
              </PermissionGuard>
            </div>
          </div>
        </div>
      )}

      {/* Modal config */}
      {configsOnly && isConfigModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                {configForm.id ? 'Editar configuração' : 'Nova configuração'}
              </Heading>
              <Button
                onClick={closeConfigModal}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
                disabled={configSaving}
              >
                <XMarkIcon className="h-4 w-4" />
                Fechar
              </Button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-5">
              {configError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
                  {configError}
                </div>
              )}

              <div>
                <Text size="sm" className="mb-2">
                  Nome <span className="text-red-500">*</span>
                </Text>
                <Input
                  value={configForm.name}
                  onChange={(e) => setConfigForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex.: Menções PASI"
                  className={configValidation.name ? 'border-red-500' : ''}
                />
                {configValidation.name && (
                  <Text size="sm" className="text-red-500 mt-1">
                    {configValidation.name}
                  </Text>
                )}
              </div>

              <div>
                <TagInput
                  label={
                    <>
                      Palavras-chave <span className="text-red-500">*</span>
                    </>
                  }
                  values={configForm.keywords}
                  onChange={(keywords) => setConfigForm((prev) => ({ ...prev, keywords }))}
                  placeholder="Digite e pressione Enter..."
                  disabled={configSaving}
                  error={configValidation.keywords}
                />
              </div>

              <div>
                <Text as="label" size="sm" className="mb-1 block">
                  Operador <span className="text-red-500">*</span>
                </Text>
                <Select
                  value={configForm.keyword_operator}
                  onChange={(e) =>
                    setConfigForm((prev) => ({
                      ...prev,
                      keyword_operator: e.target.value as ClippingSearchKeywordOperator,
                    }))
                  }
                >
                  <option value="or">Qualquer (OR)</option>
                  <option value="and">Todas (AND)</option>
                </Select>
                <Text size="xs" color="muted" className="mt-1">
                  OR: qualquer palavra já traz resultado. AND: todas devem aparecer.
                </Text>
              </div>

              <div>
                <TagInput
                  label="Excluir termos"
                  values={configForm.exclude_keywords}
                  onChange={(exclude_keywords) =>
                    setConfigForm((prev) => ({ ...prev, exclude_keywords }))
                  }
                  placeholder="Termos a ignorar (opcional)"
                  disabled={configSaving}
                />
              </div>

              <div>
                <Text size="sm" className="mb-2">
                  Categoria padrão
                </Text>
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <Select
                      value={configForm.default_category_id}
                      onChange={(e) =>
                        setConfigForm((prev) => ({ ...prev, default_category_id: e.target.value }))
                      }
                    >
                      <option value="">Selecione...</option>
                      {activeCategorias.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                      {configForm.default_category_id &&
                        !activeCategorias.some((c) => c.id === configForm.default_category_id) && (
                          <option value={configForm.default_category_id}>
                            {categorias.find((c) => c.id === configForm.default_category_id)?.name ||
                              'Categoria inativa'}{' '}
                            (inativo)
                          </option>
                        )}
                    </Select>
                  </div>
                  <PermissionGuard
                    moduleName={moduleName}
                    permissionType="insert"
                    fallback={null}
                    loadingComponent={CLIPPING_PERM_LOADING}
                  >
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onOpenCatalog('categoria', 'search_config')}
                      title="Cadastrar categoria"
                      className="shrink-0 px-2"
                      disabled={configSaving}
                    >
                      <PlusIcon className="h-4 w-4" />
                    </Button>
                  </PermissionGuard>
                </div>
                {configValidation.default_category_id && (
                  <Text size="sm" className="text-red-500 mt-1">
                    {configValidation.default_category_id}
                  </Text>
                )}
              </div>

              <div>
                <Text size="sm" className="mb-2">
                  Observações
                </Text>
                <textarea
                  value={configForm.notes}
                  onChange={(e) => setConfigForm((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Opcional"
                  rows={3}
                  className="w-full px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-white/5 ring-1 ring-gray-300 dark:ring-white/10 rounded-lg shadow-sm placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500/20"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={configForm.is_active}
                  onChange={(e) =>
                    setConfigForm((prev) => ({ ...prev, is_active: e.target.checked }))
                  }
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <Text size="sm" className="text-gray-700 dark:text-gray-300">
                  Configuração ativa
                </Text>
              </label>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <Button onClick={closeConfigModal} variant="outline" disabled={configSaving}>
                Cancelar
              </Button>
              <Button
                onClick={() => void saveConfig()}
                disabled={
                  configSaving ||
                  !configForm.name.trim() ||
                  configForm.keywords.length === 0
                }
                className="flex items-center gap-2"
                style={{ backgroundColor: BRAND_COLOR }}
              >
                {configSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4" />
                    {configForm.id ? 'Atualizar' : 'Criar'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClippingBuscaAutomaticaTab
